<script setup>
import { ref, computed, onUnmounted, watch } from 'vue';
import { useI18n } from 'vue-i18n';
import { useAlert } from 'dashboard/composables';
import getUuid from 'widget/helpers/uuid';
import NextButton from 'dashboard/components-next/button/Button.vue';

const props = defineProps({
  modelValue: {
    type: [File, Object, null],
    default: null,
  },
  existingAudioUrl: {
    type: String,
    default: '',
  },
  existingAudioFilename: {
    type: String,
    default: '',
  },
});

const emit = defineEmits(['update:modelValue', 'removeExisting']);

const { t } = useI18n();

const isRecording = ref(false);
const recordingSeconds = ref(0);
const mediaRecorder = ref(null);
const recordedChunks = ref([]);
const recordingStream = ref(null);
const recordTimer = ref(null);
const previewUrl = ref('');
const previewFilename = ref('');

const fileInput = ref(null);

const hasNewFile = computed(() => previewUrl.value && previewUrl.value.startsWith('blob:'));
const hasExistingAudio = computed(
  () => !hasNewFile.value && !!props.existingAudioUrl
);
const displayUrl = computed(() =>
  hasNewFile.value ? previewUrl.value : props.existingAudioUrl
);
const displayFilename = computed(() =>
  hasNewFile.value ? previewFilename.value : props.existingAudioFilename
);

const formattedTime = computed(() => {
  const total = recordingSeconds.value;
  const m = String(Math.floor(total / 60)).padStart(2, '0');
  const s = String(total % 60).padStart(2, '0');
  return `${m}:${s}`;
});

const setRecordedFile = blob => {
  const fileName = `audio-${getUuid()}.webm`;
  const file = new File([blob], fileName, { type: blob.type });
  previewUrl.value = URL.createObjectURL(blob);
  previewFilename.value = fileName;
  emit('update:modelValue', file);
};

const startRecording = async () => {
  try {
    const stream = await navigator.mediaDevices.getUserMedia({ audio: true });
    recordingStream.value = stream;

    const mimeType = MediaRecorder.isTypeSupported('audio/webm;codecs=opus')
      ? 'audio/webm;codecs=opus'
      : 'audio/webm';

    const recorder = new MediaRecorder(stream, { mimeType });
    recordedChunks.value = [];

    recorder.ondataavailable = e => {
      if (e.data.size > 0) recordedChunks.value.push(e.data);
    };

    recorder.onstop = () => {
      const blob = new Blob(recordedChunks.value, { type: mimeType });
      setRecordedFile(blob);
      stream.getTracks().forEach(t => t.stop());
      recordingStream.value = null;
    };

    recorder.start();
    mediaRecorder.value = recorder;
    isRecording.value = true;
    recordingSeconds.value = 0;
    recordTimer.value = setInterval(() => {
      recordingSeconds.value += 1;
    }, 1000);
  } catch (error) {
    useAlert(t('CANNED_MGMT.AUDIO.MIC_PERMISSION_ERROR'));
  }
};

const stopRecording = () => {
  if (mediaRecorder.value && mediaRecorder.value.state !== 'inactive') {
    mediaRecorder.value.stop();
  }
  clearInterval(recordTimer.value);
  recordTimer.value = null;
  isRecording.value = false;
};

const cancelRecording = () => {
  if (recordingStream.value) {
    recordingStream.value.getTracks().forEach(t => t.stop());
    recordingStream.value = null;
  }
  if (mediaRecorder.value && mediaRecorder.value.state !== 'inactive') {
    mediaRecorder.value.ondataavailable = null;
    mediaRecorder.value.onstop = null;
    mediaRecorder.value.stop();
  }
  clearInterval(recordTimer.value);
  recordTimer.value = null;
  isRecording.value = false;
  recordedChunks.value = [];
};

const onFilePicked = e => {
  const file = e.target.files?.[0];
  if (!file) return;
  if (!file.type.startsWith('audio/')) {
    useAlert(t('CANNED_MGMT.AUDIO.INVALID_FILE'));
    return;
  }
  previewUrl.value = URL.createObjectURL(file);
  previewFilename.value = file.name;
  emit('update:modelValue', file);
  e.target.value = '';
};

const triggerUpload = () => {
  fileInput.value?.click();
};

const removeAudio = () => {
  if (hasNewFile.value) {
    URL.revokeObjectURL(previewUrl.value);
    previewUrl.value = '';
    previewFilename.value = '';
    emit('update:modelValue', null);
  } else if (hasExistingAudio.value) {
    emit('removeExisting');
  }
};

watch(
  () => props.modelValue,
  newValue => {
    if (newValue === null && hasNewFile.value) {
      URL.revokeObjectURL(previewUrl.value);
      previewUrl.value = '';
      previewFilename.value = '';
    }
  }
);

onUnmounted(() => {
  if (recordTimer.value) clearInterval(recordTimer.value);
  if (recordingStream.value) {
    recordingStream.value.getTracks().forEach(t => t.stop());
  }
  if (previewUrl.value && previewUrl.value.startsWith('blob:')) {
    URL.revokeObjectURL(previewUrl.value);
  }
});
</script>

<template>
  <div class="flex flex-col gap-2">
    <input
      ref="fileInput"
      type="file"
      accept="audio/*"
      class="hidden"
      @change="onFilePicked"
    />

    <!-- Recording state -->
    <div
      v-if="isRecording"
      class="flex items-center gap-3 p-3 rounded-lg border border-n-weak bg-n-alpha-1"
    >
      <span
        class="size-3 rounded-full bg-red-500 animate-pulse flex-shrink-0"
      />
      <span class="text-sm font-mono text-n-slate-12">{{ formattedTime }}</span>
      <span class="text-sm text-n-slate-11 flex-1">
        {{ $t('CANNED_MGMT.AUDIO.RECORDING') }}
      </span>
      <NextButton
        type="button"
        size="sm"
        color-scheme="alert"
        icon="i-lucide-square"
        :label="$t('CANNED_MGMT.AUDIO.STOP')"
        @click="stopRecording"
      />
      <NextButton
        type="button"
        size="sm"
        color-scheme="secondary"
        faded
        :label="$t('CANNED_MGMT.AUDIO.CANCEL_RECORD')"
        @click="cancelRecording"
      />
    </div>

    <!-- Preview state (recorded OR uploaded OR existing) -->
    <div
      v-else-if="displayUrl"
      class="flex flex-col gap-2 p-3 rounded-lg border border-n-weak bg-n-alpha-1"
    >
      <div class="flex items-center justify-between gap-2">
        <div class="flex items-center gap-2 min-w-0 flex-1">
          <span class="i-lucide-file-audio size-4 text-n-slate-11 flex-shrink-0" />
          <span class="text-xs text-n-slate-11 truncate">
            {{ displayFilename || $t('CANNED_MGMT.AUDIO.PREVIEW_LABEL') }}
          </span>
        </div>
        <NextButton
          type="button"
          size="xs"
          color-scheme="alert"
          faded
          icon="i-lucide-trash-2"
          :title="$t('CANNED_MGMT.AUDIO.REMOVE')"
          @click="removeAudio"
        />
      </div>
      <audio :src="displayUrl" controls class="w-full" />
    </div>

    <!-- Empty state — show actions -->
    <div v-else class="flex gap-2">
      <NextButton
        type="button"
        size="sm"
        color-scheme="secondary"
        icon="i-lucide-mic"
        :label="$t('CANNED_MGMT.AUDIO.RECORD')"
        @click="startRecording"
      />
      <NextButton
        type="button"
        size="sm"
        color-scheme="secondary"
        faded
        icon="i-lucide-upload"
        :label="$t('CANNED_MGMT.AUDIO.UPLOAD')"
        @click="triggerUpload"
      />
    </div>
  </div>
</template>
